import { Inbox } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import type { ConversacionResumen } from '@/types/conversations';
import { ConversationItem } from './ConversationItem';
import { describeInboxFiltros, type InboxFiltros } from './inbox-format';

/**
 * La lista de la bandeja, con sus tres estados vacíos.
 *
 * Los tres vacíos se distinguen porque el usuario hace cosas distintas en cada uno, y el error
 * clásico es tratarlos todos como "no hay nada":
 *  1. sin filtros y sin datos → la bandeja está por estrenar (se llena cuando alguien escriba);
 *  2. con filtros → hay bandeja, pero nada coincide: lo que falta es quitar los filtros;
 *  3. sin filtros y con `total > 0`, pero la página vacía → el usuario se quedó en una página que
 *     ya no existe (las conversaciones se mueven de posición todo el tiempo, porque el orden por
 *     defecto es la última actividad): se le ofrece volver a la primera página.
 */

export interface ConversationItemsProps {
  conversaciones: ConversacionResumen[];
  /** Total de la bandeja con los filtros puestos (no el de la página). */
  total: number;
  selectedId: string | null;
  /** Ids de las conversaciones con mensajes nuevos sin ver. */
  nuevasIds: ReadonlySet<string>;
  filtering: boolean;
  filtros: InboxFiltros;
  onSelect: (conversationId: string) => void;
  onClearFiltros: () => void;
  onFirstPage: () => void;
}

export function ConversationItems({
  conversaciones,
  total,
  selectedId,
  nuevasIds,
  filtering,
  filtros,
  onSelect,
  onClearFiltros,
  onFirstPage,
}: ConversationItemsProps) {
  if (conversaciones.length === 0) {
    if (filtering) {
      return (
        <EmptyState
          icon={<Inbox size={22} />}
          title="Ninguna conversación coincide"
          description={`Con ${describeInboxFiltros(filtros)} no queda ninguna conversación en la bandeja. Prueba con otro estado del lead, otro canal u otro orden.`}
          action={
            <Button variant="secondary" size="sm" onClick={onClearFiltros}>
              Volver a la bandeja sin filtros
            </Button>
          }
        />
      );
    }

    if (total > 0) {
      return (
        <EmptyState
          icon={<Inbox size={22} />}
          title="Esta página quedó vacía"
          description="Las conversaciones se reordenan solas cada vez que alguien escribe, así que la página en la que estabas ya no tiene filas. Vuelve al principio de la bandeja."
          action={
            <Button variant="secondary" size="sm" onClick={onFirstPage}>
              Ir a la primera página
            </Button>
          }
        />
      );
    }

    return (
      <EmptyState
        icon={<Inbox size={22} />}
        title="Todavía no hay conversaciones"
        description="La bandeja se llena sola: en cuanto alguien le escriba al WhatsApp del negocio (o a Instagram o Messenger), la conversación aparece acá con su historial. El canal tiene que estar conectado en Integraciones para que el bot conteste."
      />
    );
  }

  return (
    <ul className="scroll-thin -mx-1 min-h-0 flex-1 space-y-1 overflow-y-auto px-1">
      {conversaciones.map((conversacion) => (
        <ConversationItem
          key={conversacion.id}
          conversacion={conversacion}
          selected={conversacion.id === selectedId}
          nueva={nuevasIds.has(conversacion.id)}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
}
